import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { SCREEN_LOCK_THEME } from 'system/screenLockSettings';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/**
 * Soft Lock 전역 오버레이. 세션은 유지하고 화면만 가린다.
 */
export default function LockOverlay({
  themeKey = SCREEN_LOCK_THEME.MINIMAL_DARK,
  logoUrl,
  logoAlt,
  colorMode = 'light',
  error,
  submitting,
  onSubmit,
}) {
  const now = useClock();
  const [password, setPassword] = useState('');
  const isBrand = themeKey === SCREEN_LOCK_THEME.TENANT_BRAND;
  const darkBrand = colorMode === 'dark';

  const clock = useMemo(
    () => `${pad2(now.getHours())}:${pad2(now.getMinutes())}`,
    [now]
  );
  const dateLabel = useMemo(
    () =>
      now.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'short',
      }),
    [now]
  );

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    onSubmit(password);
  };

  const rootStyle = isBrand
    ? {
        background: darkBrand ? '#121212' : '#f4f6f8',
        color: darkBrand ? '#f5f5f5' : '#1a1a1a',
      }
    : {
        background: 'linear-gradient(165deg, #0b1220 0%, #111827 55%, #020617 100%)',
        color: '#e5e7eb',
      };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="화면 잠금"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        ...rootStyle,
      }}
    >
      {isBrand && logoUrl ? (
        <img
          src={logoUrl}
          alt={logoAlt || ''}
          style={{ height: 40, maxWidth: 240, objectFit: 'contain', marginBottom: 20 }}
        />
      ) : null}
      <div style={{ fontSize: isBrand ? 56 : 72, fontWeight: 600, letterSpacing: '-0.04em', lineHeight: 1 }}>
        {clock}
      </div>
      <div style={{ marginTop: 10, fontSize: 14, opacity: 0.72 }}>{dateLabel}</div>
      <div style={{ marginTop: 28, fontSize: 13, opacity: 0.55 }}>잠금됨</div>
      <form
        onSubmit={handleSubmit}
        style={{ marginTop: 28, width: 'min(320px, calc(100% - 40px))', display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        <label style={{ fontSize: 12, opacity: 0.8 }}>비밀번호를 다시 입력하세요</label>
        <input
          type="password"
          name="lock-password"
          autoComplete="current-password"
          autoFocus
          value={password}
          disabled={submitting}
          onChange={(e) => setPassword(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 4,
            border: '1px solid rgba(255,255,255,0.22)',
            background: isBrand && !darkBrand ? '#fff' : 'rgba(0,0,0,0.28)',
            color: 'inherit',
            fontSize: 14,
          }}
        />
        {error ? (
          <div style={{ fontSize: 12, color: '#f87171' }}>{error}</div>
        ) : null}
        <button
          type="submit"
          disabled={submitting}
          style={{
            minHeight: 36,
            padding: '6px 12px',
            border: 'none',
            borderRadius: 4,
            background: isBrand && !darkBrand ? '#1b4332' : '#e5e7eb',
            color: isBrand && !darkBrand ? '#fff' : '#111827',
            fontWeight: 600,
            cursor: submitting ? 'wait' : 'pointer',
          }}
        >
          {submitting ? '확인 중…' : '잠금 해제'}
        </button>
      </form>
    </div>,
    document.body
  );
}
